import { darken, rgba } from 'polished';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Currency, InlineTooltip } from '~/components';
import { colors } from '~/styles';

const Container = styled.div`
  font-size: 0.75rem;
  flex: 1;
`;

const Legend = styled.div`
  display: flex;
  justify-content: flex-end;
  color: ${colors.grey55};
`;

const Bar = styled.div`
  height: 0.5rem;
  margin: 0.25rem 0;
  display: flex;
  border-radius: 999rem;
  overflow: hidden;
`;

const Segment = styled.div`
  position: relative;
  display: block;
  width: ${({ $width }) => $width};
  background-color: ${({ color }) => color};
  transition:
    width 0.5s ease-out,
    background-color 0.25s ease-in-out;

  &:hover {
    box-shadow: 0 3px 15px ${rgba(colors.grey100, 0.25)};
    background-color: ${({ color }) => darken(0.05, color)};
  }
`;

export default function ClientApprovalExpensesProgress({ clientApproval }) {
  const progress = useMemo(() => {
    const total = clientApproval.totalExpenseItems ?? 0;
    const approved = clientApproval.approvedExpenseItems ?? 0;
    const rejected = clientApproval.rejectedExpenseItems ?? 0;
    const pending = clientApproval.pendingExpenseItems ?? 0;
    const pct = (value) => (total ? `${(value / total) * 100}%` : '0%');
    return {
      total,
      approved,
      rejected,
      pending,
      approvedPercentage: pct(approved),
      rejectedPercentage: pct(rejected),
      pendingPercentage: pct(pending),
    };
  }, [clientApproval]);

  if (progress.total === 0) return <span>—</span>;

  const currency = clientApproval.project?.currency;

  return (
    <Container>
      <Legend>
        <Currency value={clientApproval.totalExpenses ?? 0} currency={currency} />
      </Legend>
      <Bar>
        <Segment $width={progress.approvedPercentage} color={colors.success}>
          <InlineTooltip message={`${progress.approved} approved`} placement="top" />
        </Segment>
        <Segment $width={progress.rejectedPercentage} color={colors.danger}>
          <InlineTooltip message={`${progress.rejected} rejected`} placement="top" />
        </Segment>
        <Segment $width={progress.pendingPercentage} color={colors.grey5} />
      </Bar>
      <Legend>
        {progress.total} {pluralize('expense item', progress.total)}
      </Legend>
    </Container>
  );
}
